import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { TRPCClientError } from "@trpc/client";
import { toast } from "sonner";
import { Temporal } from "temporal-polyfill";
import { useTRPC } from "@/trpc";
import { saveBlob } from "@/ajax/documents/saveBlob";
import {
  vaultScopeKey,
  type CredentialVaultScope,
} from "../lib/credential-vault-scope";
import type { VaultFile } from "../types";
import {
  collectVaultFiles,
  createVaultArchive,
  fetchVaultFile,
  uniqueVaultFiles,
  validateVaultDownload,
  vaultDownloadLimits,
  vaultDownloadName,
  type DownloadedVaultFile,
  type VaultDownloadFailure,
} from "../lib/vault-file-downloads";

export function useVaultFileDownloads({
  scope,
}: {
  scope: CredentialVaultScope;
}) {
  const trpc = useTRPC();
  const heldUrl = useMutation(
    trpc.credentials.getMyDocumentUrl.mutationOptions(),
  );
  const reviewUrl = useMutation(
    trpc.credentials.getMyReviewDocumentUrl.mutationOptions(),
  );
  const orgHeldUrl = useMutation(
    trpc.credentials.getDocumentUrlForBu.mutationOptions(),
  );
  const orgReviewUrl = useMutation(
    trpc.credentials.getReviewDocumentUrlForBu.mutationOptions(),
  );
  const scopeKey = vaultScopeKey(scope);
  const limits = vaultDownloadLimits(Capacitor.isNativePlatform());
  const [isBusy, setIsBusy] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null,
  );
  const [progress, setProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);
  const [failures, setFailures] = useState<VaultDownloadFailure[]>([]);
  const [successfulCount, setSuccessfulCount] = useState(0);
  const collected = useRef<DownloadedVaultFile[]>([]);
  const active = useRef<AbortController | null>(null);
  useEffect(() => {
    setIsBusy(false);
    setDownloadingFileId(null);
    setProgress(null);
    setFailures([]);
    setSuccessfulCount(0);
    return () => {
      active.current?.abort();
      active.current = null;
      collected.current = [];
    };
  }, [scopeKey]);

  async function mintUrl(file: VaultFile): Promise<string> {
    const input = { documentId: file.id, asAttachment: true };
    const readHeld = () =>
      scope.kind === "organization"
        ? orgHeldUrl.mutateAsync({
            ...input,
            organizationBusinessUnitId: scope.organizationBusinessUnitId,
            providerUserId: scope.providerUserId,
            ...(scope.expectedAffiliationId
              ? { expectedAffiliationId: scope.expectedAffiliationId }
              : {}),
            credentialViewId: scope.credentialViewId,
          })
        : heldUrl.mutateAsync({ ...input, switchUserId: scope.userId });
    const readReview = () =>
      scope.kind === "organization"
        ? orgReviewUrl.mutateAsync({
            ...input,
            organizationBusinessUnitId: scope.organizationBusinessUnitId,
            providerUserId: scope.providerUserId,
            ...(scope.expectedAffiliationId
              ? { expectedAffiliationId: scope.expectedAffiliationId }
              : {}),
          })
        : reviewUrl.mutateAsync({ ...input, switchUserId: scope.userId });
    if (file.isReviewDocument) {
      try {
        return (await readReview()).signedUrl;
      } catch (error) {
        // Review may finish after listing or between export retries. The held
        // endpoint must independently authorize the now-attached document.
        if (
          !(error instanceof TRPCClientError) ||
          error.data?.code !== "NOT_FOUND"
        ) {
          throw error;
        }
      }
    }
    return (await readHeld()).signedUrl;
  }

  function dismissFailures() {
    if (active.current) {
      return;
    }
    collected.current = [];
    setFailures([]);
    setSuccessfulCount(0);
  }

  async function run(action: (signal: AbortSignal) => Promise<void>) {
    if (active.current) {
      return;
    }
    const controller = new AbortController();
    active.current = controller;
    setIsBusy(true);
    try {
      await action(controller.signal);
    } catch (error) {
      if (!controller.signal.aborted) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Could not download these files.",
        );
      }
    } finally {
      if (active.current === controller) {
        active.current = null;
        setIsBusy(false);
        setDownloadingFileId(null);
        setProgress(null);
      }
    }
  }

  async function saveArchive(
    files: DownloadedVaultFile[],
    signal: AbortSignal,
  ) {
    if (files.length === 0) {
      return;
    }
    signal.throwIfAborted();
    const blob = await createVaultArchive(files);
    signal.throwIfAborted();
    await saveBlob({
      blob,
      fileName: `Saile-credential-files-${Temporal.Now.plainDateISO()}.zip`,
    });
    signal.throwIfAborted();
    collected.current = [];
    setFailures([]);
    setSuccessfulCount(0);
    toast.success(
      `Downloaded ${files.length} file${files.length === 1 ? "" : "s"}`,
    );
  }

  async function collect(files: VaultFile[], signal: AbortSignal) {
    const result = await collectVaultFiles({
      files,
      initialFiles: collected.current,
      limits,
      mintUrl,
      signal,
      onProgress: (next) => {
        if (!signal.aborted) {
          setProgress(next);
        }
      },
    });
    signal.throwIfAborted();
    collected.current = result.successful;
    setFailures(result.failures);
    setSuccessfulCount(result.successful.length);
    if (result.failures.length === 0) {
      await saveArchive(result.successful, signal);
    }
  }

  async function downloadFile(file: VaultFile) {
    await run(async (signal) => {
      validateVaultDownload({ files: [file], limits });
      setDownloadingFileId(file.id);
      const result = await fetchVaultFile({
        file,
        mintUrl,
        maxBytes: limits.maxTotalBytes,
        signal,
      });
      signal.throwIfAborted();
      await saveBlob({
        blob: result.blob,
        fileName: vaultDownloadName(file.fileName),
      });
    });
  }

  async function downloadFiles(files: VaultFile[]) {
    await run(async (signal) => {
      const unique = uniqueVaultFiles(files);
      validateVaultDownload({ files: unique, limits });
      collected.current = [];
      setFailures([]);
      setSuccessfulCount(0);
      await collect(unique, signal);
    });
  }

  async function retryFailed() {
    await run((signal) =>
      collect(
        failures.map((failure) => failure.file),
        signal,
      ),
    );
  }

  async function downloadSuccessful() {
    await run((signal) => saveArchive(collected.current, signal));
  }

  return {
    downloadFile,
    downloadFiles,
    isBusy,
    downloadingFileId,
    progress,
    failures,
    successfulCount,
    retryFailed,
    downloadSuccessful,
    dismissFailures,
    limits,
  };
}
